import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Buildings,
  Plus,
  CheckCircle,
  XCircle,
  Eye,
  EyeSlash,
  FloppyDisk,
  Plugs,
  ArrowsClockwise,
  Users as UsersIcon,
  Briefcase,
  Trash,
  Warning,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECRET_FIELDS = [
  { key: "instantly_api_key", label: "Instantly API Key", placeholder: "Instantly bearer token" },
  { key: "ghl_api_key", label: "GoHighLevel API Key", placeholder: "GHL Private Integration Token" },
  { key: "ghl_location_id", label: "GHL Location ID", placeholder: "GHL Location ID", isPlain: true },
];

const STAGE_KEYS = [
  { key: "new_lead", label: "New Lead", desc: "Initial stage when lead is pushed" },
  { key: "contacted", label: "Contacted", desc: "First outreach email sent" },
  { key: "replied", label: "Replied", desc: "Lead replied to email" },
  { key: "meeting_booked", label: "Meeting Booked", desc: "Meeting has been scheduled" },
  { key: "closed_lost", label: "Closed Lost", desc: "Bounced or unsubscribed" },
];

const OrganizationsPage = () => {
  const { getAuthHeaders, user, isOrgOverrideActive, activeOrgId } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [orgDetails, setOrgDetails] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [editingName, setEditingName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [savingIntegrations, setSavingIntegrations] = useState(false);
  const [integrationsForm, setIntegrationsForm] = useState({});
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [ghlSettings, setGhlSettings] = useState({ pipeline_id: "", stage_mapping: {} });
  const [savingGhl, setSavingGhl] = useState(false);
  const [testingInstantly, setTestingInstantly] = useState(false);
  const [testingGhl, setTestingGhl] = useState(false);
  const [testResult, setTestResult] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, slug, counts, is_default, is_home_org }
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchOrgs();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      fetchOrgDetails(selectedOrgId);
    }
  }, [selectedOrgId]);

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, { headers: getAuthHeaders() });
      setOrgs(res.data || []);
      if (res.data?.length && !selectedOrgId) {
        setSelectedOrgId(res.data[0].id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetails = async (orgId) => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}`, { headers: getAuthHeaders() });
      setOrgDetails(res.data);
      setEditingName(res.data.name);
      // integrations is the un-masked dict when caller can edit
      setIntegrationsForm({ ...(res.data.integrations || {}) });
      setGhlSettings({
        pipeline_id: res.data.ghl_settings?.pipeline_id || "",
        stage_mapping: res.data.ghl_settings?.stage_mapping || {},
      });
      setShowSecrets({});
      setTestResult({});
      // Try to load pipelines/stages once GHL keys are present
      if (res.data.integrations?.ghl_api_key && res.data.integrations?.ghl_location_id) {
        fetchPipelines(orgId);
        if (res.data.ghl_settings?.pipeline_id) {
          fetchStages(orgId, res.data.ghl_settings.pipeline_id);
        }
      } else {
        setPipelines([]);
        setStages([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load organization");
    }
  };

  const fetchPipelines = async (orgId) => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/ghl/pipelines`, { headers: getAuthHeaders() });
      setPipelines(res.data || []);
    } catch {
      setPipelines([]);
    }
  };

  const fetchStages = async (orgId, pipelineId) => {
    if (!pipelineId) {
      setStages([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/ghl/pipelines/${pipelineId}/stages`, {
        headers: getAuthHeaders(),
      });
      setStages(res.data || []);
    } catch {
      setStages([]);
    }
  };

  const handleCreateOrg = async () => {
    const name = newOrgName.trim();
    if (!name) {
      toast.error("Organization name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/organizations`, { name }, { headers: getAuthHeaders() });
      toast.success(`Organization "${res.data.name}" created`);
      setNewOrgName("");
      await fetchOrgs();
      setSelectedOrgId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create organization");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveName = async () => {
    if (!orgDetails) return;
    const name = editingName.trim();
    if (!name) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await axios.put(`${API}/organizations/${orgDetails.id}`, { name }, { headers: getAuthHeaders() });
      toast.success("Name updated");
      await fetchOrgs();
      await fetchOrgDetails(orgDetails.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveIntegrations = async () => {
    if (!orgDetails) return;
    setSavingIntegrations(true);
    try {
      // Only send fields that look populated (whitespace stripped); empty strings clear nothing
      const payload = {};
      for (const f of SECRET_FIELDS) {
        const v = (integrationsForm[f.key] || "").trim();
        if (v) payload[f.key] = v;
      }
      await axios.put(
        `${API}/organizations/${orgDetails.id}/integrations`,
        payload,
        { headers: getAuthHeaders() }
      );
      toast.success("Integrations saved");
      await fetchOrgDetails(orgDetails.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save integrations");
    } finally {
      setSavingIntegrations(false);
    }
  };

  const handleSaveGhl = async () => {
    if (!orgDetails) return;
    setSavingGhl(true);
    try {
      await axios.put(
        `${API}/organizations/${orgDetails.id}/ghl-settings`,
        ghlSettings,
        { headers: getAuthHeaders() }
      );
      toast.success("GHL settings saved");
      await fetchOrgDetails(orgDetails.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save GHL settings");
    } finally {
      setSavingGhl(false);
    }
  };

  const handleTestInstantly = async () => {
    if (!orgDetails) return;
    setTestingInstantly(true);
    setTestResult({ ...testResult, instantly: null });
    try {
      const res = await axios.post(
        `${API}/organizations/${orgDetails.id}/test/instantly`,
        {},
        { headers: getAuthHeaders() }
      );
      setTestResult({ ...testResult, instantly: { ok: true, msg: res.data.message } });
      toast.success("Instantly connected");
    } catch (err) {
      setTestResult({ ...testResult, instantly: { ok: false, msg: err.response?.data?.detail || "Failed" } });
      toast.error(err.response?.data?.detail || "Instantly test failed");
    } finally {
      setTestingInstantly(false);
    }
  };

  const handleTestGhl = async () => {
    if (!orgDetails) return;
    setTestingGhl(true);
    setTestResult({ ...testResult, ghl: null });
    try {
      const res = await axios.post(
        `${API}/organizations/${orgDetails.id}/test/ghl`,
        {},
        { headers: getAuthHeaders() }
      );
      const data = res.data || {};
      // New shape (v2): {status, message, scopes:[{name, ok, status_code, scope, hint}]}
      // Legacy shape: {status, message}
      const isOk = data.status === "success";
      setTestResult({
        ...testResult,
        ghl: {
          ok: isOk,
          msg: data.message || (isOk ? "GHL connected" : "Partial connection"),
          scopes: data.scopes || null,
          status: data.status || (isOk ? "success" : "error"),
        },
      });
      if (isOk) toast.success("GHL connected — all scopes verified");
      else if (data.status === "partial") toast.error(`GHL partial: ${data.message}`, { duration: 8000 });
      else toast.error(data.message || "GHL test failed");
      // Reload pipelines after a successful/partial test (contacts+opportunities may work)
      fetchPipelines(orgDetails.id);
    } catch (err) {
      setTestResult({ ...testResult, ghl: { ok: false, msg: err.response?.data?.detail || "Failed", status: "error" } });
      toast.error(err.response?.data?.detail || "GHL test failed");
    } finally {
      setTestingGhl(false);
    }
  };

  const handlePipelineChange = (pipelineId) => {
    setGhlSettings(prev => ({ ...prev, pipeline_id: pipelineId, stage_mapping: {} }));
    if (orgDetails) fetchStages(orgDetails.id, pipelineId);
  };

  const handleStageMapping = (key, stageId) => {
    setGhlSettings(prev => ({
      ...prev,
      stage_mapping: { ...prev.stage_mapping, [key]: stageId },
    }));
  };

  const requestDelete = async (org) => {
    setDeleteTarget({ id: org.id, name: org.name, slug: org.slug });
    setDeleteConfirmText("");
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API}/organizations/${org.id}/cascade-preview`, {
        headers: getAuthHeaders(),
      });
      setDeleteTarget(prev => prev ? { ...prev, ...res.data } : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load delete preview");
      setDeleteTarget(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/organizations/${deleteTarget.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(res.data?.message || "Organization deleted");
      setDeleteTarget(null);
      setDeleteConfirmText("");
      // If we just deleted the currently-viewed org, pick another
      if (selectedOrgId === deleteTarget.id) {
        setSelectedOrgId("");
        setOrgDetails(null);
      }
      await fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete organization");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="organizations-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Organizations
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Each org has its own Instantly, GoHighLevel, LeadFlow Scraper and verification keys.</p>
        </div>
        <div className="text-xs text-slate-500 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
          <Buildings size={14} className="text-[#0EA5E9]" />
          <span>{orgs.length} {orgs.length === 1 ? "org" : "orgs"} total</span>
        </div>
      </div>

      {/* Two-column layout: compact org list on the left, editor on the right.
          Left column becomes sticky on desktop so you can jump between orgs
          without losing the editor context. On mobile it collapses to a
          normal stack. */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 items-start">
        {/* --------- LEFT: create + list --------- */}
        <div className="space-y-3 lg:sticky lg:top-4">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass-card border-slate-200">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-medium">
                  <Plus size={14} className="text-[#006994]" /> New organization
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && newOrgName.trim()) handleCreateOrg(); }}
                    placeholder="e.g. Acme Co"
                    className="flex-1 h-9 text-sm bg-white/50 border-slate-200 text-slate-900"
                    data-testid="new-org-name-input"
                  />
                  <Button
                    onClick={handleCreateOrg}
                    disabled={creating || !newOrgName.trim()}
                    size="sm"
                    className="h-9 px-3 bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                    data-testid="create-org-btn"
                  >
                    {creating ? "…" : "Add"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="glass-card border-slate-200">
              <CardHeader className="pb-2 pt-3 px-3">
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-medium">
                  <Buildings size={14} className="text-[#0EA5E9]" /> All organizations
                </div>
              </CardHeader>
              <CardContent className="p-2 space-y-1" data-testid="orgs-list">
                {orgs.map(o => {
                  const isDefault = o.slug === "dolphin-digital";
                  const isHomeOrg = o.id === user?.organization_id;
                  const isOverridingThis = isOrgOverrideActive && o.id === activeOrgId;
                  const canDelete = !isDefault && !isHomeOrg && !isOverridingThis;
                  const isSelected = selectedOrgId === o.id;
                  return (
                    <div
                      key={o.id}
                      className={`group flex items-stretch gap-1 rounded-md border transition-colors ${
                        isSelected
                          ? "bg-[#006994]/10 border-[#006994]/40"
                          : "bg-slate-50 border-slate-200 hover:border-slate-200"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedOrgId(o.id)}
                        className="flex-1 text-left px-2.5 py-2 min-w-0"
                        data-testid={`org-row-${o.slug}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-900 text-sm truncate flex items-center gap-1.5">
                            <span className="truncate">{o.name}</span>
                            {isDefault && (
                              <span className="text-[9px] uppercase tracking-wider text-[#006994] border border-[#006994]/40 rounded px-1 flex-shrink-0">default</span>
                            )}
                            {isHomeOrg && !isDefault && (
                              <span className="text-[9px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded px-1 flex-shrink-0">home</span>
                            )}
                          </p>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {o.integrations?.instantly_configured ? (
                              <span title="Instantly OK" className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            ) : (
                              <span title="Instantly not configured" className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                            )}
                            {o.integrations?.ghl_configured ? (
                              <span title="GHL OK" className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            ) : (
                              <span title="GHL not configured" className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <UsersIcon size={10} /> {o.user_count ?? 0}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Briefcase size={10} /> {o.job_count ?? 0}
                          </span>
                          <span className="font-mono truncate text-slate-400">{o.slug}</span>
                        </div>
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); requestDelete(o); }}
                          className="px-2 text-slate-400 hover:text-red-400 hover:bg-red-500/5 rounded-r-md transition-colors opacity-0 group-hover:opacity-100"
                          title={`Delete ${o.name}`}
                          data-testid={`org-delete-btn-${o.slug}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* --------- RIGHT: editor for the selected org --------- */}
        <div className="space-y-4 min-w-0">
      {/* Org details + edit */}
      {orgDetails && (
        <>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 text-lg">{orgDetails.name}</CardTitle>
                <CardDescription className="text-slate-500 font-mono text-xs">{orgDetails.slug}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-slate-500 text-xs">Name</Label>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="bg-white/50 border-slate-200 text-slate-900"
                      data-testid="edit-org-name-input"
                    />
                  </div>
                  <Button
                    onClick={handleSaveName}
                    disabled={savingName || !editingName.trim() || editingName === orgDetails.name}
                    className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                    data-testid="save-org-name-btn"
                  >
                    {savingName ? "..." : "Save"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Integrations */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
                  <Plugs size={20} className="text-[#006994]" /> Integration Keys
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Per-organization credentials for Instantly and GoHighLevel. The LeadFlow Scraper, MillionVerifier and SendGrid are managed globally by the super admin in Settings.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {SECRET_FIELDS.map(f => (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-slate-700 text-sm">{f.label}</Label>
                    <div className="relative">
                      <Input
                        type={f.isPlain || showSecrets[f.key] ? "text" : "password"}
                        value={integrationsForm[f.key] || ""}
                        onChange={(e) => setIntegrationsForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900 font-mono text-sm pr-10"
                        data-testid={`org-integration-${f.key}`}
                      />
                      {!f.isPlain && (
                        <button
                          type="button"
                          onClick={() => setShowSecrets(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                        >
                          {showSecrets[f.key] ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button
                    onClick={handleSaveIntegrations}
                    disabled={savingIntegrations}
                    className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                    data-testid="save-org-integrations-btn"
                  >
                    <FloppyDisk size={16} className="mr-1" />
                    {savingIntegrations ? "Saving..." : "Save Integrations"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleTestInstantly}
                    disabled={testingInstantly}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100"
                    data-testid="test-org-instantly-btn"
                  >
                    {testingInstantly ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : <Plugs size={16} className="mr-1" />}
                    Test Instantly
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleTestGhl}
                    disabled={testingGhl}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100"
                    data-testid="test-org-ghl-btn"
                  >
                    {testingGhl ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : <Plugs size={16} className="mr-1" />}
                    Test GHL
                  </Button>
                </div>

                {(testResult.instantly || testResult.ghl) && (
                  <div className="space-y-2 pt-2">
                    {testResult.instantly && (
                      <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${testResult.instantly.ok ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                        {testResult.instantly.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        Instantly: {testResult.instantly.msg}
                      </div>
                    )}
                    {testResult.ghl && (
                      <div className={`p-3 rounded-lg text-sm ${testResult.ghl.ok ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : testResult.ghl.status === "partial" ? "bg-amber-500/10 text-amber-500 border border-amber-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`} data-testid="test-ghl-result">
                        <div className="flex items-center gap-2 font-medium">
                          {testResult.ghl.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
                          GHL: {testResult.ghl.msg}
                        </div>
                        {testResult.ghl.scopes && testResult.ghl.scopes.length > 0 && (
                          <div className="mt-2 pl-6 space-y-1">
                            {testResult.ghl.scopes.map((s, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs" data-testid={`test-ghl-scope-${i}`}>
                                <span className={`mt-0.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.ok ? "bg-emerald-500" : s.status_code === 404 ? "bg-slate-400" : "bg-red-500"}`} />
                                <div className="flex-1">
                                  <span className="font-semibold text-slate-800">{s.name}</span>
                                  <span className="text-slate-500 ml-2">
                                    {s.ok ? "OK" : `HTTP ${s.status_code || "err"}`}
                                  </span>
                                  {!s.ok && s.hint && (
                                    <div className="text-slate-600 mt-0.5">{s.hint}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* GHL pipeline + stages */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
                  <Plugs size={20} className="text-[#0EA5E9]" /> GoHighLevel Pipeline
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Map LeadFlow stages to your GHL pipeline (per-org)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Pipeline</Label>
                  <Select value={ghlSettings.pipeline_id || ""} onValueChange={handlePipelineChange}>
                    <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900" data-testid="org-ghl-pipeline-select">
                      <SelectValue placeholder={pipelines.length === 0 ? "Configure GHL keys + Test GHL first" : "Select pipeline"} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {pipelines.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-slate-900 hover:bg-slate-100">{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {ghlSettings.pipeline_id && stages.length > 0 && (
                  <div className="space-y-3 p-4 rounded-lg bg-white border border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700">Stage Mapping</h4>
                    {STAGE_KEYS.map(stage => (
                      <div key={stage.key} className="flex items-center gap-3">
                        <div className="w-36 flex-shrink-0">
                          <p className="text-sm text-slate-900">{stage.label}</p>
                          <p className="text-xs text-slate-500">{stage.desc}</p>
                        </div>
                        <Select
                          value={ghlSettings.stage_mapping?.[stage.key] || ""}
                          onValueChange={(v) => handleStageMapping(stage.key, v)}
                        >
                          <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900 text-sm" data-testid={`org-ghl-stage-${stage.key}`}>
                            <SelectValue placeholder="Select stage..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            {stages.map(s => (
                              <SelectItem key={s.id} value={s.id} className="text-slate-900 hover:bg-slate-100">{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={handleSaveGhl}
                  disabled={savingGhl || !ghlSettings.pipeline_id}
                  className="bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 font-bold"
                  data-testid="save-org-ghl-btn"
                >
                  {savingGhl ? "Saving..." : "Save GHL Settings"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}

      {!orgDetails && (
        <div className="glass-card border border-slate-200 rounded-lg p-10 text-center text-slate-500 text-sm" data-testid="orgs-empty-editor">
          Select an organization on the left to view and edit its integrations, pipeline, and stage mapping.
        </div>
      )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="delete-org-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Warning size={20} className="text-red-400" /> Delete Organization?
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This permanently deletes <span className="font-bold text-slate-900">{deleteTarget?.name}</span> and ALL of its data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {loadingPreview ? (
            <div className="py-6 flex items-center justify-center text-slate-500 gap-2">
              <ArrowsClockwise size={16} className="animate-spin" /> Counting records...
            </div>
          ) : deleteTarget?.counts ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3">
                <p className="text-xs text-red-300 uppercase tracking-wider font-medium mb-2">Records that will be wiped</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(deleteTarget.counts).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500 capitalize">{k.replace("_", " ")}</span>
                      <span className={`font-mono ${v > 0 ? "text-red-300" : "text-slate-400"}`}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">
                  Type <span className="font-mono text-slate-900">{deleteTarget.name}</span> to confirm
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.name}
                  autoFocus
                  className="bg-white/50 border-slate-200 text-slate-900"
                  data-testid="delete-org-confirm-input"
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="delete-org-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deleting || loadingPreview || deleteConfirmText !== (deleteTarget?.name || "")}
              className="bg-red-500 hover:bg-red-600 text-slate-900 font-bold disabled:opacity-50"
              data-testid="delete-org-confirm-btn"
            >
              {deleting ? "Deleting..." : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationsPage;
